import { useNavigate } from "react-router-dom";
import { ComponentPropsWithoutRef, useCallback, useMemo, useRef } from "react";
import { isMatrixCommandStep, isParallelCommandStep } from "app/lib/pipeline";
import Icon from "app/components/shared/Icon";
import { CommandJob } from "app/components/build/Show/lib/types";
import { useTrack } from "app/components/build/Show/lib/useTrack";
import {
  findJobsForStep,
  mostRecentMatrixJobs,
  mostRecentParallelJobs,
  useBuild,
} from "app/components/build/Show/lib/BuildContext";
import useQueryParams from "app/components/build/Show/lib/useQueryParams";
import { resolveJobUrl, resolveStepUrl } from "app/components/build/Show/lib/urlForView";
import {
  FilterByOption,
  filterSteps,
} from "app/components/build/Show/components/Sidebar/useFilterStore";
import { useHotkeys } from "react-hotkeys-hook";

/**
 * Get the list of current failing steps (and jobs) in the build path.
 *
 * @returns
 */
export const useFailurePaths = (): { step: string; job?: string }[] => {
  const { build } = useBuild();

  return useMemo(() => {
    return (
      filterSteps(build.steps, [FilterByOption.Failed, FilterByOption.SoftFailed], build)
        .filter((step) => step.type !== "group")
        .flatMap((step) => {
          // Non-sidebar canvas only supports jumping to steps rather than jobs.
          if (!Features.BuildSidebar) {
            return { step: step.uuid };
          }

          // Find the latest parallel failed jobs for the step.
          if (isParallelCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentParallelJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          // Find the latest matrix failed jobs for the step.
          if (isMatrixCommandStep(step)) {
            const jobs = findJobsForStep<CommandJob>(build, step.uuid).filter(
              (job) => job.state === "finished" && !job.passed,
            );
            return mostRecentMatrixJobs(jobs).map((job) => ({
              job: job.id,
              step: step.uuid,
            }));
          }

          return { step: step.uuid };
        }) || []
    );
  }, [build.steps, build?.jobs]);
};

interface GoToFailureProps extends Omit<ComponentPropsWithoutRef<"button">, "onClick"> {
  className?: string;
}

export const GoToFailure = ({ className = "", ...props }: GoToFailureProps) => {
  const ref = useRef<HTMLButtonElement>(null);
  const failures = useFailurePaths();
  const { stepUuid, jobUuid } = useQueryParams();
  const navigate = useNavigate();
  const track = useTrack();

  const focusOnFailure = useCallback(() => {
    if (!failures.length) {
      return;
    }

    let nextFailure: { step: string; job?: string };

    const current = failures.find((failure) => {
      if (jobUuid) {
        return failure.job === jobUuid;
      }
      if (stepUuid) {
        return failure.step === stepUuid;
      }
      return false;
    });

    if (!current) {
      nextFailure = failures[0];
    } else {
      const idx = failures.indexOf(current);

      if (idx + 1 < failures.length) {
        nextFailure = failures[idx + 1];
      } else {
        nextFailure = failures[0];
      }
    }

    track("Click Go To Failure");

    // Blur the button to prevent focus issues when navigating.
    if (ref.current) {
      ref.current.blur();
    }

    // Navigate to the step or job based on the failure.
    if (nextFailure.job) {
      navigate(resolveJobUrl(nextFailure.job), { state: { forceFocus: true } });
    } else {
      navigate(resolveStepUrl(nextFailure.step), { state: { forceFocus: true } });
    }
  }, [failures, stepUuid, jobUuid, navigate, track]);

  useHotkeys("f", focusOnFailure);

  return (
    <button
      {...props}
      ref={ref}
      onClick={focusOnFailure}
      disabled={!failures.length}
      title="Go to failure (F)"
      className={`hover:bg-text-600 group inline-flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 font-medium transition-all hover:bg-purple-100 disabled:cursor-not-allowed disabled:opacity-50 ${className} `}
    >
      <Icon icon="custom/outline/redo-dot" className="h-4 w-4 group-hover:stroke-purple-600" />
    </button>
  );
};
